import { debugFlag } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import { SHOW_TOOLBAR_KEY } from '@/shared/constants/appConstants';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { atom, getDefaultStore } from 'jotai';
import type { SetStateAction } from 'react';

// Lazy import to avoid circular dependency:
// gridSettingsAtom -> focusGrid -> gridSettings -> store -> gridSettingsAtom
const lazyFocusGrid = () => import('@/app/helpers/focusGrid').then((m) => m.focusGrid());

export const SETTINGS_KEY = 'viewSettings';

export type GridSettings = {
  showHeadings: boolean;
  showGridLines: boolean;
  showCellTypeOutlines: boolean;
  showA1Notation: boolean;
  showToolbar: boolean;
  showFormulaBar: boolean;
  presentationMode: boolean;
  showAIAnalystOnStartup: boolean;
  showScrollbars: boolean;
  restoreFileViewState: boolean;
  /** Excel's Page Break Preview: shows where pages break and lets them be dragged. */
  pageBreakPreview: boolean;
};

export const defaultGridSettings: GridSettings = {
  showHeadings: true,
  showGridLines: true,
  showCellTypeOutlines: false,
  showA1Notation: false,
  showToolbar: true,
  showFormulaBar: true,
  presentationMode: false,
  showAIAnalystOnStartup: true,
  showScrollbars: true,
  restoreFileViewState: false,
  pageBreakPreview: false,
};

const gridSettingsBaseAtom = atom<GridSettings>(defaultGridSettings);

export const gridSettingsAtom = atom(
  (get) => get(gridSettingsBaseAtom),
  (get, set, update: SetStateAction<GridSettings>) => {
    const newValue = typeof update === 'function' ? update(get(gridSettingsBaseAtom)) : update;
    if (debugFlag('debugGridSettings')) console.log('[gridSettings] setting new value', newValue);
    set(gridSettingsBaseAtom, newValue);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(newValue));
    events.emit('gridSettings');
  }
);

let gridSettingsInitialized = false;

export const initGridSettingsFromStorage = () => {
  if (gridSettingsInitialized) return;
  gridSettingsInitialized = true;

  const store = getDefaultStore();
  const savedValue = localStorage.getItem(SETTINGS_KEY);

  const legacyToolbarValue = localStorage.getItem(SHOW_TOOLBAR_KEY);

  if (savedValue != null) {
    try {
      const settings = JSON.parse(savedValue);
      const newSettings = { ...defaultGridSettings, ...settings, presentationMode: false };
      if (legacyToolbarValue != null) {
        try {
          const showToolbar = JSON.parse(legacyToolbarValue);
          if (typeof showToolbar === 'boolean') {
            newSettings.showToolbar = showToolbar;
          }
        } catch {
          // Ignore invalid legacy toolbar setting.
        }
        localStorage.removeItem(SHOW_TOOLBAR_KEY);
      }
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings));
      if (debugFlag('debugGridSettings'))
        console.log('[gridSettings] initializing with values from localStorage', newSettings);
      store.set(gridSettingsBaseAtom, newSettings);
      events.emit('gridSettings');
    } catch (error) {
      console.warn('[gridSettings] Failed to parse saved settings, using defaults:', error);
      localStorage.removeItem(SETTINGS_KEY);
    }
  } else if (legacyToolbarValue != null) {
    try {
      const showToolbar = JSON.parse(legacyToolbarValue);
      if (typeof showToolbar === 'boolean') {
        const newSettings = { ...defaultGridSettings, showToolbar };
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings));
        store.set(gridSettingsBaseAtom, newSettings);
        events.emit('gridSettings');
      }
    } catch {
      // Ignore invalid legacy toolbar setting.
    }
    localStorage.removeItem(SHOW_TOOLBAR_KEY);
  }
};

const settingEventMap: Partial<Record<keyof GridSettings, string>> = {
  showHeadings: '[Settings].toggleShowHeadings',
  showGridLines: '[Settings].toggleShowGridLines',
  showScrollbars: '[Settings].toggleShowScrollbars',
  showCellTypeOutlines: '[Settings].toggleShowCellTypeOutlines',
  showToolbar: '[Settings].toggleShowToolbar',
  showFormulaBar: '[Settings].toggleShowFormulaBar',
  pageBreakPreview: '[Settings].togglePageBreakPreview',
};

const createSettingAtom = <T extends keyof GridSettings>(key: T) =>
  atom(
    (get) => get(gridSettingsAtom)[key],
    (get, set, update: SetStateAction<GridSettings[T]>) => {
      const prev = get(gridSettingsAtom)[key];
      const newValue =
        typeof update === 'function' ? (update as (prev: GridSettings[T]) => GridSettings[T])(prev) : update;
      if (settingEventMap[key]) {
        trackEvent(settingEventMap[key]!, { enabled: newValue });
      }
      set(gridSettingsAtom, { ...get(gridSettingsAtom), [key]: newValue });
      lazyFocusGrid();
    }
  );

export const showHeadingsAtom = createSettingAtom('showHeadings');
export const showGridLinesAtom = createSettingAtom('showGridLines');
export const showCellTypeOutlinesAtom = createSettingAtom('showCellTypeOutlines');
export const showA1NotationAtom = createSettingAtom('showA1Notation');
export const showToolbarAtom = createSettingAtom('showToolbar');
export const showFormulaBarAtom = createSettingAtom('showFormulaBar');
export const presentationModeAtom = createSettingAtom('presentationMode');
export const showScrollbarsAtom = createSettingAtom('showScrollbars');
export const restoreFileViewStateAtom = createSettingAtom('restoreFileViewState');
export const pageBreakPreviewAtom = createSettingAtom('pageBreakPreview');

export const showAIAnalystOnStartupAtom = atom(
  (get) => get(gridSettingsAtom).showAIAnalystOnStartup,
  (get, set, update: SetStateAction<boolean>) => {
    const prev = get(gridSettingsAtom).showAIAnalystOnStartup;
    const newValue = typeof update === 'function' ? update(prev) : update;
    trackEvent('[Settings].toggleShowAIAnalystOnStartup', { enabled: newValue });
    set(gridSettingsAtom, { ...get(gridSettingsAtom), showAIAnalystOnStartup: newValue });
    // Lazy import to avoid circular dependency: gridSettingsAtom -> aiAnalystAtoms -> effectAtoms -> focusGrid -> gridSettings -> store -> gridSettingsAtom
    import('@/app/ai/atoms/aiAnalystAtoms').then(({ aiStore, showAIAnalystAtom }) => {
      aiStore.set(showAIAnalystAtom, newValue);
    });
    lazyFocusGrid();
  }
);
